import {startCase, omit} from 'lodash';
import {
  TText,
  TRecord,
  TList,
  TElement,
  ExpressionElement,
  TTypeConstructor,
  TBoolean,
} from '@packages/systems/wfdl-ast';
import * as React from 'react';
import {sanitizeProp} from '@packages/utilities/react-sanitize';
import {
  Atom,
  Expressions,
  withMeta,
  withSystem,
  type AttributesObject,
} from '@packages/systems/wfdl';
import {BuiltinFactory} from '@packages/systems/wfdl-builtin-plugin';

import {
  qualify,
  iconName,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {WarningCircleIcon} from '@packages/systems/spring';
import {useTheme} from '@packages/utilities/styled';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {
  useOpenCanvas,
  usePreviewMode,
  useRuntimeEnvironment,
  useStyleTagOpenCanvasRewriter,
} from '@packages/systems/render';
import {VOID_HTML_TAGS} from '@packages/systems/core/constants/SharedConfig';
import {notNil} from '@packages/utilities/fp/utils';
import {integratedVisibilityType} from '@packages/utilities/integrated-types';
import {getChildren} from '@packages/utilities/data-utils';

function ErrorMessage(props: AttributesObject) {
  const {colors, typography, gutters} = useTheme();

  return (
    <div
      {...props}
      style={{
        background: 'rgba(228, 47, 58, 0.10)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: gutters.x8,
        gap: 10,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          background: colors.dangerBackground,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: gutters.x8,
          gap: gutters.x4,
          borderRadius: gutters.x4,
          color: '#F5F5F5',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            alignSelf: 'stretch',
            width: 16,
            paddingBottom: 0,
          }}
        >
          <WarningCircleIcon />
        </div>
        <div
          style={{
            fontFamily: typography.copy.fontFamily,
            fontSize: '12.5px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '16px' /* 128% */,
          }}
        >
          This element couldn‘t be rendered because it may not support child
          elements, or it has an invalid tag.
        </div>
      </div>
    </div>
  );
}

type ErrorInfo = {
  componentStack: string;
};

type ErrorBoundaryProps = {
  children: React.ReactNode;
  tag: string;
  className?: string;
};

const ErrorBoundary = (props: ErrorBoundaryProps) => {
  return (
    <React.Suspense fallback={null}>
      <ErrorBoundaryClient {...props} />
    </React.Suspense>
  );
};

class ErrorBoundaryClient extends React.PureComponent<
  ErrorBoundaryProps,
  {error: null | Error}
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {error: null};
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return {error};
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (
      this.props.tag !== prevProps.tag ||
      this.props.children !== prevProps.children
    ) {
      this.setState({error: null});
    }
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error(_error, _errorInfo);
  }

  render() {
    const {error} = this.state;
    const {children, tag, className: _, ...rest} = this.props;

    if (error) {
      return <ErrorMessage tag={tag} {...rest} />;
    }

    return children;
  }
}

const attributesType = withMeta(
  {validationMode: 'permissive'},
  withSystem(
    {
      label: 'Attributes',
      isBindable: true,
      labelVariant: 'hidden',
      defaultValue: [],
    },
    TTypeConstructor(qualify(pluginName('Basic'), typeName('Attributes')))
  )
);

function isClassAttributeName(name: string) {
  const lowerCaseName = name.toLowerCase();
  return lowerCaseName === 'class' || lowerCaseName === 'classname';
}

function attrsDOMReducer(
  memo: Record<string, string>,
  {
    name,
    value,
  }: {
    name: string;
    value: string;
  }
) {
  if (name === 'is' || name === 'dangerouslySetInnerHTML') return memo;

  // React expects style to be an object; the work around is to pass our style
  // string as STYLE instead.
  if (name === 'style' && typeof value === 'string') {
    memo.STYLE = value;
  } else {
    memo[name] = sanitizeProp(name, value);
  }
  return memo;
}

// Validates HTML tag names according to HTML specifications.
// 1. Standard HTML Elements: Tag names are composed of ASCII alphabetic characters.
//    Specification: https://html.spec.whatwg.org/multipage/syntax.html#tag-name
// 2. Custom Elements: Must contain a hyphen and cannot start with a number.
//    Specification: https://html.spec.whatwg.org/multipage/custom-elements.html#valid-custom-element-name
// The regex ensures that the tag starts with an alphabetical character,
// followed by zero or more alphanumeric characters or hyphens.
const TAG_VALIDATION_REGEX = /^[a-zA-Z][a-zA-Z0-9-]*$/;
const validationRegex = new RegExp(TAG_VALIDATION_REGEX);

const DOM = Atom.create({
  render: Atom.createRender(
    ({data, generateAttributes, attributes, classes, renderChild}) => {
      const runtimeEnvironment = useRuntimeEnvironment();
      const isOpenCanvas = useOpenCanvas();
      const rewriteStyleTagForOpenCanvas = useStyleTagOpenCanvasRewriter();

      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      const attrs = (data.attributes ?? []).reduce(attrsDOMReducer, {});
      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      const slot = data.slot ?? null;

      let props: AttributesObject = {
        ...generateAttributes(attributes, classes),
        ...attrs,
        ...(slot ? {slot} : {}),
      };
      const isPreviewMode = usePreviewMode();

      // Normally we'd default to React's standard className prop, but in Custom Element
      // a user may set the Tag to be a Custom HTML element (aka Web component).
      // From the react docs: "Custom elements accept class rather than className..." (https://react.dev/reference/react-dom/components#custom-html-elements).
      // "class" has been supported in React since v16, so to be able to support Custom Element and standard DOM elements
      // we can use the class prop for both. See https://github.com/facebook/react/issues/13525#issuecomment-417818906,
      // Custom `class` attributes can come through with different casing.
      const generatedClassName = props.className;
      const classAttributeNames =
        Object.keys(attrs).filter(isClassAttributeName);
      const classAttribute = classAttributeNames
        .map((name) => {
          const value = attrs[name];
          return typeof value === 'string' ? value : '';
        })
        .filter(Boolean)
        .join(' ');

      // Remove class-like props from the spread object, default to class instead (see large comment block above).
      props = omit(props, 'className', ...classAttributeNames);

      if (generatedClassName || classAttribute) {
        props.class = `${generatedClassName || ''} ${classAttribute}`.trim();
      }

      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      if (data.tag === undefined || !validationRegex.test(data.tag)) {
        return <ErrorMessage {...props} />;
      }

      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      const Tag = data.tag || 'div';
      const tagName = Tag.toLowerCase();

      // Don't render script tags in Designer
      if (
        runtimeEnvironment === WebflowEnvironments.DESIGNER &&
        tagName === 'script'
      ) {
        return null;
      }

      // if in Designer remove pointer-events from the iframe
      if (
        tagName === 'iframe' &&
        !isPreviewMode &&
        runtimeEnvironment === WebflowEnvironments.DESIGNER
      ) {
        props.style = {...props.style, pointerEvents: 'none'};
      }

      // HTML void elements do not accept children and will cause React to throw an error.
      // In the Designer, we rely on this error in order for ErrorBoundary to
      // render appropriate error messages on canvas.
      // In all other environments, children should be excluded when rendering void elements.
      const children = getChildren(data);
      const hasChildren = children.length > 0;
      const shouldRenderChildren =
        hasChildren &&
        (runtimeEnvironment === WebflowEnvironments.DESIGNER ||
          !shouldRenderWithoutChildren(tagName, props, children));

      const renderedChildren = shouldRenderChildren
        ? children.map((child) => renderChild(child))
        : null;
      const renderedStyleChildren =
        tagName === 'style' &&
        isOpenCanvas &&
        renderedChildren?.every((child) => typeof child === 'string')
          ? rewriteStyleTagForOpenCanvas(renderedChildren.join(''))
          : renderedChildren;

      return (
        <ErrorBoundary {...props} tag={Tag}>
          {renderedChildren != null ? (
            <Tag {...props}>{renderedStyleChildren}</Tag>
          ) : (
            <Tag {...props} />
          )}
        </ErrorBoundary>
      );
    }
  ),
  dataType: TRecord({
    tag: withSystem(
      {label: 'Tag', isBindable: true, isLocalizable: true},
      TTypeConstructor(qualify(pluginName('Basic'), typeName('TagType')))
    ),
    attributes: attributesType,
    children: withSystem(
      {label: 'Text', isBindable: true, isLocalizable: true},
      TList(TElement)
    ),
    slot: withSystem(
      {label: 'Slot', isBindable: false, private: true, defaultValue: ''},
      TText
    ),
    text: withSystem({private: true, defaultValue: false}, TBoolean),
    visibility: integratedVisibilityType,
  }),
  icon: {
    type: 'Component',
    value: qualify(pluginName('Builtin'), iconName('CustomElement')),
  },
  displayName: {
    type: 'Derived',
    defaultValue: 'Custom Element',
    getFromElement(element: ExpressionElement) {
      const data = Expressions.getElementData(element);
      const fields = Expressions.getRecord(data);
      if (!fields) return 'Custom Element';
      const tagExpr = fields.tag;
      if (!tagExpr) return 'Custom Element';
      const tag = Expressions.getText(tagExpr);
      return tag ? startCase(tag) : 'Custom Element';
    },
  },
  showEmpty: true,
});

function shouldRenderWithoutChildren(
  tagName: string,
  props: {[key: string]: unknown},
  children?: readonly unknown[]
) {
  // Special cases where children should be excluded to prevent React errors and follow HTML specs

  // For textareas, React throws an error if both children and value/defaultValue are provided
  const isTextareaWithValueOrDefaultValue =
    tagName === 'textarea' &&
    notNil(children) &&
    (notNil(props.value) || notNil(props.defaultValue));

  // Return true if any special case applies or if it's a void HTML tag
  // Void HTML tags (like <img>, <input>, etc.) cannot have children by HTML spec
  // Without this check, React would throw errors when rendering void elements with children
  return isTextareaWithValueOrDefaultValue || VOID_HTML_TAGS.has(tagName);
}

const DangerousDOMRender = Atom.createRender(
  ({data, generateAttributes, attributes, classes}) => {
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const Tag = data.tag;

    if (Tag === undefined) {
      throw new Error(
        'Builtin DangerousDOM `tag` is undefined. Make sure to pass in a valid `tag` to the DangerousDOM‘s `data` Record.'
      );
    }

    const props = {
      ...generateAttributes(attributes, classes),
      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      ...(data.attributes ?? []).reduce(attrsDOMReducer, {}),
      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      dangerouslySetInnerHTML: data.value ? {__html: data.value} : null,
    };

    return <Tag {...props} />;
  }
);

const DangerousDOM = Atom.create({
  render: DangerousDOMRender,
  dataType: TRecord({
    /** HTML tag to render, eg <script> */
    tag: TText,
    /** This is the innerHTML value to set dangerously */
    value: TText,
    /** List of HTML attributes to render on the element */
    attributes: attributesType,
  }),
  showEmpty: false,
});

export const plugin = BuiltinFactory(DOM, DangerousDOM);
